/**
 * «Kamera devori» — every kiosk lobby on one screen.
 *
 * ============================================================================
 * THE TILE MUST ALWAYS SAY WHY IT IS DARK.
 * ============================================================================
 *
 * A camera wall has exactly one failure mode that matters, and it is not a
 * broken decoder: it is a black rectangle that an operator reads as "nothing is
 * happening there". Four very different things make a tile dark —
 *
 *   the camera is OFF          → somebody chose that; it is not a fault
 *   the kiosk is OFFLINE       → send a technician
 *   a citizen is MID-MUROJAAT  → §3.1; the machine is working perfectly
 *   the frame has gone STALE   → the camera is on and the frames stopped
 *
 * — and only two of them are problems. So every dark tile carries its reason in
 * words. The one that would otherwise be misread is the third: a citizen
 * exercising their right to complain in private looks exactly like a failure,
 * and the reasonable response to a failure is to send somebody to the building.
 *
 * The pictures are `<img>` tags re-fetched on a timer, not a video stream. A
 * kiosk sends a frame every six seconds; a wall of 35 WebRTC peers would need a
 * media server, and the thing an operator actually does here is scan tiles for
 * the one that looks wrong — which is a six-second question, not a 30 fps one.
 */
import { useCallback, useEffect, useState } from 'react';
import type { components } from '@navoiy/api-types';
import { api, ApiError, apiBaseUrl, unwrap } from '../api/client.js';
import { useSession } from '../auth/SessionContext.js';
import { t } from '../i18n/index.js';
import { ageLabel } from '../lib/format.js';
import { Button, EmptyState, Notice, Panel, PanelHeader, Skeleton, StatusPill } from '../ui/index.jsx';
import { KpiCard, PageHeader } from '../ui/console.jsx';
import { LiveBadge } from '../ui/data.jsx';
import { NoSignalLayer, SearchingSignal } from '../ui/nosignal.jsx';
import { IconClose } from '../ui/icons.jsx';

type MbtCameraTile = components['schemas']['MbtCameraTile'];

/**
 * How often each tile re-fetches its picture while the wall is open.
 *
 * 400 ms, matching the kiosk's live capture rate. That is a wall an operator
 * SCANS for the tile that looks wrong, which is a two-frame-a-second task —
 * not thirty, which would multiply the load on a shared VPS to make a lobby
 * look smoother than the question being asked of it.
 */
const FRAME_MS = 400;

/** How often the tile METADATA (status, queue, camera state) reloads. */
const META_MS = 5_000;

/**
 * How often to tell the server the wall is still open.
 *
 * The server's window is 15 s, so renewing every 5 s survives two lost
 * requests. Stopping simply lets it lapse — there is no "stop watching" call to
 * forget or to fail, which is what makes a closed laptop stop the cameras.
 */
const WATCH_MS = 5_000;

/**
 * A frame older than this is not the present.
 *
 * Three capture intervals. One missed frame is a dropped packet; three in a row
 * is a camera that has stopped, and showing the last good picture past that
 * point is the wall telling an operator a room is calm when nobody knows.
 */
const STALE_MS = 20_000;

export function CameraWallScreen(): React.JSX.Element {
  const { expire, allows } = useSession();
  const onExpired = useCallback(() => expire(), [expire]);
  const maySwitch = allows('mbt.command.issue');

  const [items, setItems] = useState<MbtCameraTile[] | null>(null);
  const [scoped, setScoped] = useState(false);
  const [error, setError] = useState<'forbidden' | 'network' | null>(null);
  const [now, setNow] = useState(() => Date.now());
  /** Bumped on every refresh; appended to each `<img>` URL to defeat caching. */
  const [tick, setTick] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  /**
   * The camera opened full-size, if any.
   *
   * A tile is 320 px wide on a 1440 px screen; a lobby at that size answers
   * «is anybody there» and nothing else. An operator who has spotted something
   * needs to LOOK, and until now the only way to look closer was to lean in.
   *
   * Modal rather than a route, for the reason the screen wall's is: it is a
   * look, not a destination, and a URL that survived a reload would leave a
   * lobby camera enlarged on whoever restored that tab.
   */
  const [focused, setFocused] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data = unwrap(await api().GET('/mbt/fleet/cameras', {}));
      setItems(data.items as MbtCameraTile[]);
      setScoped(data.scoped);
      setError(null);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) onExpired();
      else if (caught instanceof ApiError && caught.status === 403) setError('forbidden');
      else setError('network');
    }
  }, [onExpired]);

  // Metadata: status, camera state, queue depth. Slow — none of it is video.
  useEffect(() => {
    void load();
    const timer = setInterval(() => {
      setNow(Date.now());
      void load();
    }, META_MS);
    return () => clearInterval(timer);
  }, [load]);

  /**
   * The picture itself, at the live rate. Separated from the metadata poll so a
   * 400 ms frame does not drag a database query along behind it.
   */
  useEffect(() => {
    const timer = setInterval(() => setTick((previous) => previous + 1), FRAME_MS);
    return () => clearInterval(timer);
  }, []);

  /**
   * Hold the watch window open while this screen is mounted.
   *
   * The cleanup deliberately does NOTHING: leaving is expressed by no longer
   * renewing, so a crashed tab, a closed laptop and a navigated-away operator
   * all stop the cameras identically. A "stop watching" call would be a promise
   * this screen cannot keep.
   */
  useEffect(() => {
    const ids = (items ?? []).filter((tile) => tile.camera_enabled).map((tile) => tile.device_id);
    if (ids.length === 0) return undefined;
    const renew = () => {
      void api()
        .POST('/mbt/fleet/cameras/watch', { body: { device_ids: ids } })
        .catch(() => {
          // A missed renewal costs one lapsed window, and the next tick fixes
          // it. Never surfaced: an operator watching a wall does not need to be
          // told about a dropped keep-alive.
        });
    };
    renew();
    const timer = setInterval(renew, WATCH_MS);
    return () => clearInterval(timer);
  }, [items]);

  /** Escape closes the enlarged view — the key everybody presses first. */
  useEffect(() => {
    if (focused === null) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setFocused(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [focused]);

  async function toggle(tile: MbtCameraTile): Promise<void> {
    setBusy(tile.device_id);
    try {
      await api().POST('/mbt/fleet/cameras/{device_id}', {
        params: { path: { device_id: tile.device_id } },
        body: { enabled: !tile.camera_enabled },
      });
      await load();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) onExpired();
      else setError('network');
    } finally {
      setBusy(null);
    }
  }

  if (error === 'forbidden') {
    return <Notice tone="crit">{t('camera.noPermission')}</Notice>;
  }

  const live = items?.filter((tile) => tile.camera_enabled).length ?? 0;
  /* A tile is LIVE only if its camera is on, the kiosk is up, nobody is
     recording, and the newest frame is fresh — the same four conditions the
     tile itself applies, kept in one place so the counter and the grid can
     never disagree. */
  const liveTiles =
    items?.filter(
      (tile) =>
        tile.camera_enabled &&
        tile.online &&
        !tile.appeal_in_progress &&
        tile.frame_at !== undefined &&
        now - new Date(tile.frame_at).getTime() <= STALE_MS,
    ).length ?? 0;
  const staleTiles =
    items?.filter(
      (tile) =>
        tile.camera_enabled &&
        tile.online &&
        !tile.appeal_in_progress &&
        (tile.frame_at === undefined || now - new Date(tile.frame_at).getTime() > STALE_MS),
    ).length ?? 0;

  const focusedTile = items?.find((tile) => tile.device_id === focused) ?? null;

  return (
    <div className="space-y-4">
      <PageHeader
        eyebrow={
          <>
            <LiveBadge label={t('status.live')} />
            <span>{t('camera.eyebrow')}</span>
          </>
        }
        title={t('nav.camera')}
        subtitle={t('camera.subtitle', { live, total: items?.length ?? 0 })}
        right={
          <p className="text-hero font-bold tabular-nums leading-none text-ink-100">{live}</p>
        }
      />

      {/*
        The four states a tile can be in, as counters.

        This is the same taxonomy the tiles themselves use, and it is here
        because the wall's failure mode is a screen of dark rectangles an
        operator has to read one by one to classify. Two of these are faults and
        two are not, and the counters say how many of each before anybody starts
        counting squares.
      */}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          label={t('camera.kpiLive')}
          value={String(liveTiles)}
          absent={t('app.notYet')}
          tone={liveTiles > 0 ? 'ok' : 'neutral'}
          footerLabel={t('camera.kpiEnabled')}
          footerValue={String(live)}
        />
        <KpiCard
          label={t('camera.kpiOff')}
          value={String((items ?? []).filter((tile) => !tile.camera_enabled).length)}
          absent={t('app.notYet')}
          tone="neutral"
          footerLabel={t('camera.kpiOfTotal')}
          footerValue={String(items?.length ?? 0)}
        />
        <KpiCard
          label={t('camera.kpiAppeal')}
          /* NOT a fault, and counted separately for that reason: a citizen
             recording a murojaat is the system working, and lumping it with
             the broken cameras is how somebody gets dispatched to a healthy
             building. */
          value={String((items ?? []).filter((tile) => tile.appeal_in_progress).length)}
          absent={t('app.notYet')}
          tone="info"
          footerLabel={t('camera.kpiNotFault')}
          footerValue=""
        />
        <KpiCard
          label={t('camera.kpiStale')}
          value={String(staleTiles)}
          absent={t('app.notYet')}
          tone={staleTiles > 0 ? 'warn' : 'ok'}
          footerLabel={t('status.offline')}
          footerValue={String((items ?? []).filter((tile) => !tile.online).length)}
        />
      </div>

      {error === 'network' ? <Notice tone="warn">{t('error.network')}</Notice> : null}
      {scoped ? <Notice tone="quiet">{t('fleet.scopedNote')}</Notice> : null}

      {/* §5.1 said out loud, on the screen that does the watching. An operator
          who never sees this sentence is an operator who forgets what the wall
          is. */}
      <Notice tone="quiet">{t('camera.privacyNote')}</Notice>

      <Panel>
        <PanelHeader title={t('nav.camera')} aside={t('camera.hint')} />
        <div className="px-4 py-4">
          {items === null ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
              {[0, 1, 2, 3, 4, 5].map((key) => (
                <Skeleton key={key} className="h-64" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <EmptyState message={t('camera.empty')} />
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
              {items.map((tile) => (
                <CameraTile
                  key={tile.device_id}
                  tile={tile}
                  now={now}
                  tick={tick}
                  maySwitch={maySwitch}
                  busy={busy === tile.device_id}
                  onToggle={() => void toggle(tile)}
                  onExpand={() => setFocused(tile.device_id)}
                />
              ))}
            </div>
          )}
        </div>
      </Panel>

      {focusedTile === null ? null : (
        <FocusedCamera
          tile={focusedTile}
          now={now}
          tick={tick}
          maySwitch={maySwitch}
          busy={busy === focusedTile.device_id}
          onToggle={() => void toggle(focusedTile)}
          onClose={() => setFocused(null)}
        />
      )}
    </div>
  );
}

/**
 * Is this dark state a LOST SIGNAL, or a deliberate one?
 *
 * ============================================================================
 * ONLY THE FIRST GETS SNOW, AND THE DISTINCTION IS THE WHOLE POINT.
 * ============================================================================
 *
 * TV static means «there is supposed to be a picture here and there is not».
 * That is exactly true of a camera somebody switched off, a kiosk that is
 * unreachable, and a feed that stopped — and it is exactly FALSE of a tile that
 * is dark because a citizen is filing a murojaat. Putting snow on that one
 * would dress a privacy guarantee up as a malfunction, which is the same
 * mistake as calling it «stale»: the reasonable response to a malfunction is to
 * send somebody to the building.
 *
 * So the privacy plate stays clean navy with a padlock on it, and the snow says
 * what snow has always said.
 */
const NO_SIGNAL = new Set(['off', 'offline', 'stale']);

/**
 * The glyph for a dark tile.
 *
 * Drawn here rather than imported: these four marks exist nowhere else in the
 * app, and a word alone was not enough. The wall's failure mode is a grid of
 * dark rectangles an operator classifies one at a time — a mark is read at a
 * glance across a room, which is how these screens are actually used, and the
 * word underneath is what makes the mark unambiguous.
 */
function DarkGlyph({ kind }: { readonly kind: string }) {
  const common = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.4, strokeLinecap: 'round' as const };
  return (
    <svg viewBox="0 0 24 24" className="relative h-9 w-9" aria-hidden="true">
      {kind === 'off' ? (
        <>
          {/* A camera body with a stroke through it — switched off, by somebody. */}
          <rect x="2.5" y="7" width="13" height="10" rx="2" {...common} />
          <path d="M15.5 11l5-3v8l-5-3" {...common} />
          <path d="M3 20L21 4" {...common} />
        </>
      ) : kind === 'appeal' ? (
        /* A padlock. The one dark state that is a PROMISE rather than a fault:
           a citizen is recording a complaint and the wall is not watching. */
        <>
          <rect x="5" y="10.5" width="14" height="9.5" rx="2" {...common} />
          <path d="M8.5 10.5V7.5a3.5 3.5 0 017 0v3" {...common} />
        </>
      ) : (
        /* A clock. The frames stopped; the camera thinks it is still on. */
        <>
          <circle cx="12" cy="12" r="8.5" {...common} />
          <path d="M12 7.5V12l3 2" {...common} />
        </>
      )}
    </svg>
  );
}

/** One labelled fact under a tile — the same shape the ad cards use. */
function TileStat({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div className="min-w-0">
      <p className="truncate text-nano uppercase text-ink-500">{label}</p>
      <p className="truncate text-label font-semibold tabular-nums text-ink-100">{value}</p>
    </div>
  );
}

function CameraTile({
  tile,
  now,
  tick,
  maySwitch,
  busy,
  onToggle,
  onExpand,
}: {
  readonly tile: MbtCameraTile;
  readonly now: number;
  readonly tick: number;
  readonly maySwitch: boolean;
  readonly busy: boolean;
  readonly onToggle: () => void;
  readonly onExpand: () => void;
}) {
  const frameAge = tile.frame_at ? now - new Date(tile.frame_at).getTime() : null;
  const stale = frameAge === null || frameAge > STALE_MS;

  /**
   * The four reasons a tile is dark, in the order an operator needs them: the
   * ones that are NOT faults first, so nobody dispatches a technician to a
   * kiosk whose camera somebody deliberately switched off.
   */
  const dark = !tile.camera_enabled
    ? { key: 'off', label: t('camera.off'), tone: 'text-ink-500' }
    : tile.appeal_in_progress
      ? { key: 'appeal', label: t('camera.appealPrivacy'), tone: 'text-brand' }
      : !tile.online
        ? { key: 'offline', label: t('camera.offline'), tone: 'text-offline' }
        : stale
          ? { key: 'stale', label: t('camera.stale'), tone: 'text-warn' }
          : null;

  return (
    <div className="flex flex-col overflow-hidden rounded-card border border-hairline bg-panel shadow-card transition-shadow duration-state hover:shadow-lift">
      {/*
        THE PICTURE IS THE BUTTON, not the whole card.

        The card already contains a control — the camera's on/off switch — and a
        button inside a button is invalid markup that behaves differently in
        every browser. Making the media region the target also matches what an
        operator means when they reach for a lobby: they are pointing at the
        picture, not at the row of counters under it.

        `group` so the hover hint can fade in over the image without a second
        piece of state.
      */}
      <button
        type="button"
        onClick={onExpand}
        title={t('camera.expand')}
        className="group relative block aspect-video w-full cursor-zoom-in bg-canvas"
      >
        {dark === null ? (
          <img
            // `tick` in the query string is what makes an <img> re-fetch. The
            // API sends `no-store`, but a browser that has the URL already
            // painted will not ask again without a changed one.
            src={`${apiBaseUrl()}/mbt/fleet/cameras/${tile.device_id}/frame?v=${tick}`}
            alt={t('camera.frameAlt', { kiosk: tile.label })}
            className="h-full w-full object-cover"
          />
        ) : (
          /*
            THE DARK PLATE — navy and gold, not an empty rectangle.

            A tile with no picture used to be the panel's own background with a
            small word floating in it, which reads as a component that failed to
            render. On the console's own navy, with a mark and a word, it reads
            as a state the system is deliberately in — which for two of the four
            reasons is exactly true.
          */
          <div
            className={`relative flex h-full w-full flex-col items-center justify-center gap-1.5 bg-brand-deep px-3 text-center ${
              NO_SIGNAL.has(dark.key) ? 'text-white' : `bg-hero ${dark.tone}`
            }`}
          >
            {NO_SIGNAL.has(dark.key) ? (
              <NoSignalLayer />
            ) : null}
            {/* `relative` on the content, so it sits ABOVE the snow layer
                rather than under it — and the label carries its own scrim,
                because small caps over a field of grain is unreadable. */}
            {/* An unreachable machine is not a dead input — it is a device that
                is not answering — so it gets a signal that is visibly looking
                for one. Every other dark state keeps its still mark. */}
            {dark.key === 'offline' ? <SearchingSignal /> : <DarkGlyph kind={dark.key} />}
            <span className="relative rounded bg-brand-deep/80 px-1.5 py-0.5 text-nano uppercase tracking-wide">
              {dark.label}
            </span>
          </div>
        )}

        <span className="absolute left-2 top-2 rounded bg-canvas/85 px-1.5 py-0.5 text-nano font-semibold tabular-nums text-ink-300">
          {tile.short_code}
        </span>
        {dark === null ? (
          <span className="absolute right-2 top-2 flex items-center gap-1 rounded bg-ok/90 px-1.5 py-0.5 text-nano font-semibold uppercase text-white">
            <span className="h-1.5 w-1.5 animate-live rounded-full bg-white" />
            {t('camera.liveTag')}
          </span>
        ) : null}
        {/* The affordance, on hover only. A permanent «kattalashtirish» label
            would sit on top of the one thing this tile exists to show. */}
        <span className="pointer-events-none absolute inset-x-0 bottom-0 bg-canvas/80 py-1 text-nano uppercase tracking-wide text-ink-300 opacity-0 transition-opacity duration-state group-hover:opacity-100">
          {t('camera.expand')}
        </span>
      </button>

      <div className="flex min-w-0 flex-1 flex-col gap-3 p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate text-label font-semibold text-ink-100">{tile.label}</p>
            <p className="truncate text-micro text-ink-500">{tile.mahalla_name}</p>
          </div>
          <StatusPill status={tile.status} label={t(`status.${tile.status}`)} />
        </div>

        {/* THREE LABELLED FACTS, not three bare numbers in a row. The old line
            read «2 · 7 · 4 daqiqa» and an operator had to remember which was
            which; the same grid the ad cards use gives each one its name. */}
        <div className="grid grid-cols-3 gap-2">
          <TileStat label={t('camera.statQueue')} value={String(tile.queue_depth ?? 0)} />
          <TileStat label={t('camera.statToday')} value={String(tile.appeals_today ?? 0)} />
          {/* The «as of» travels with the picture, always. A frame without one
              is a still image pretending to be a live feed. */}
          <TileStat
            label={t('camera.statFrame')}
            value={tile.frame_at ? ageLabel(new Date(tile.frame_at), new Date(now), t) : '—'}
          />
        </div>

        {maySwitch ? (
          <div className="mt-auto border-t border-hairline pt-3">
            <Button
              tone={tile.camera_enabled ? 'quiet' : 'primary'}
              onClick={onToggle}
              disabled={busy}
            >
              {tile.camera_enabled ? t('camera.turnOff') : t('camera.turnOn')}
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * One lobby, as large as the window allows.
 *
 * ============================================================================
 * IT REFRESHES ON THE SAME `tick` AS THE WALL, AND DOES NOT ASK FOR MORE.
 * ============================================================================
 *
 * The obvious thing would be to poll this faster than the grid. It already IS
 * the fast rate: the wall renews a watch window for every enabled camera while
 * it is open, and a watched camera captures at `LIVE_INTERVAL_MS` (400 ms) —
 * so the frames arriving for the tile are the same frames this needs. A second
 * timer would re-fetch the same picture twice and double the bandwidth from a
 * mahalla for no extra information.
 *
 * §5's privacy line is unchanged by enlarging: this is the same frame the wall
 * was already showing, at the size a screen can show it. Nothing new is
 * captured, nothing new is stored, and closing it changes nothing on the kiosk
 * — the watch window belongs to the wall, not to this.
 */
function FocusedCamera({
  tile,
  now,
  tick,
  maySwitch,
  busy,
  onToggle,
  onClose,
}: {
  readonly tile: MbtCameraTile;
  readonly now: number;
  readonly tick: number;
  readonly maySwitch: boolean;
  readonly busy: boolean;
  readonly onToggle: () => void;
  readonly onClose: () => void;
}) {
  const frameAge = tile.frame_at ? now - new Date(tile.frame_at).getTime() : null;
  const stale = frameAge === null || frameAge > STALE_MS;
  const dark = !tile.camera_enabled
    ? { key: 'off', label: t('camera.off'), tone: 'text-ink-500' }
    : tile.appeal_in_progress
      ? { key: 'appeal', label: t('camera.appealPrivacy'), tone: 'text-brand' }
      : !tile.online
        ? { key: 'offline', label: t('camera.offline'), tone: 'text-offline' }
        : stale
          ? { key: 'stale', label: t('camera.stale'), tone: 'text-warn' }
          : null;

  return (
    /*
      ==========================================================================
      FULL VIEWPORT, WITH A SHEET DOWN THE RIGHT.
      ==========================================================================

      The first cut was a centred card with a max height, which on a 1440px
      monitor made the lobby about twice a tile — better, and still not what
      «expand» means. An operator opening this is trying to SEE something: a
      face, a queue, whether a door is open. That wants every pixel the display
      has.

      So the picture takes the whole window and the detail moves into a sheet on
      the right, which is where a detail panel belongs beside a large image and
      is the one arrangement that costs the picture nothing but its own width.

      The frame is `object-contain` on black: a lobby camera is evidence, and
      cropping evidence to fill a shape is the one thing this view must not do.
    */
    <div
      className="fixed inset-0 z-50 flex bg-black"
      role="dialog"
      aria-modal="true"
      aria-label={t('camera.frameAlt', { kiosk: tile.label })}
    >
      {/* THE PICTURE, taking everything the sheet does not. */}
      <div className="relative min-w-0 flex-1">
        {dark === null ? (
          <img
            src={`${apiBaseUrl()}/mbt/fleet/cameras/${tile.device_id}/frame?v=${tick}`}
            alt={t('camera.frameAlt', { kiosk: tile.label })}
            className="absolute inset-0 h-full w-full object-contain"
          />
        ) : (
          <div
            className={`absolute inset-0 flex flex-col items-center justify-center gap-3 bg-brand-deep text-center ${
              NO_SIGNAL.has(dark.key) ? 'text-white' : `bg-hero ${dark.tone}`
            }`}
          >
            {NO_SIGNAL.has(dark.key) ? <NoSignalLayer /> : null}
            {dark.key === 'offline' ? <SearchingSignal /> : <DarkGlyph kind={dark.key} />}
            <span className="relative rounded bg-brand-deep/80 px-3 py-1 text-lead uppercase tracking-wide">
              {dark.label}
            </span>
          </div>
        )}

        {dark === null ? (
          <span className="absolute left-4 top-4 flex items-center gap-2 rounded bg-black/60 px-2.5 py-1 text-label font-semibold uppercase text-ok">
            <span className="h-2 w-2 animate-live rounded-full bg-ok" />
            {t('camera.liveTag')}
          </span>
        ) : null}

        {/* The short code over the picture, because at full screen the sheet is
            out of the eye's way and «which kiosk am I looking at» must not need
            a glance sideways. */}
        <span className="absolute bottom-4 left-4 rounded bg-black/60 px-2.5 py-1 text-label font-semibold tabular-nums text-white">
          {tile.short_code}
        </span>
      </div>

      {/* ---------------------------------------------------------------- */}
      {/* THE SHEET                                                          */}
      {/* ---------------------------------------------------------------- */}
      <aside className="flex w-80 shrink-0 flex-col gap-4 overflow-y-auto border-l border-hairline bg-panel p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate text-lead font-semibold text-ink-100">{tile.label}</p>
            <p className="truncate text-micro text-ink-500">{tile.mahalla_name}</p>
          </div>
          {/* A REAL CLOSE CONTROL. Esc and a backdrop click are both gone at
              full screen — there is no backdrop left to click — so the way out
              has to be visible. */}
          <button
            type="button"
            onClick={onClose}
            title={t('camera.close')}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded text-ink-500 transition-colors duration-state hover:bg-panel-raised hover:text-ink-100"
          >
            <IconClose className="h-5 w-5" />
          </button>
        </div>

        <StatusPill status={tile.status} label={t(`status.${tile.status}`)} />

        <dl className="space-y-2 text-label">
          <div className="flex items-baseline justify-between gap-2 border-b border-hairline pb-2">
            <dt className="text-micro text-ink-500">{t('camera.statQueue')}</dt>
            <dd className="text-lead font-semibold tabular-nums text-ink-100">
              {tile.queue_depth ?? 0}
            </dd>
          </div>
          <div className="flex items-baseline justify-between gap-2 border-b border-hairline pb-2">
            <dt className="text-micro text-ink-500">{t('camera.statToday')}</dt>
            <dd className="text-lead font-semibold tabular-nums text-ink-100">
              {tile.appeals_today ?? 0}
            </dd>
          </div>
          <div className="flex items-baseline justify-between gap-2">
            <dt className="text-micro text-ink-500">{t('camera.statFrame')}</dt>
            <dd className="text-label font-semibold tabular-nums text-ink-100">
              {tile.frame_at ? ageLabel(new Date(tile.frame_at), new Date(now), t) : '—'}
            </dd>
          </div>
        </dl>

        {maySwitch ? (
          <Button
            tone={tile.camera_enabled ? 'quiet' : 'primary'}
            onClick={onToggle}
            disabled={busy}
          >
            {tile.camera_enabled ? t('camera.turnOff') : t('camera.turnOn')}
          </Button>
        ) : null}

        {/* §5, on the screen that does the watching — and never more relevant
            than when one lobby is filling a display. */}
        <p className="mt-auto text-micro leading-relaxed text-ink-500">
          {t('camera.privacyNote')}
        </p>
        <p className="text-micro text-ink-500">{t('camera.closeHint')}</p>
      </aside>
    </div>
  );
}
